/**
 * Role Detail Page
 *
 * Displays role information with a permission matrix for viewing and assigning
 * permissions. Supports the 5-tier role hierarchy.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft as BackIcon } from 'lucide-react';
import { Alert, Card, Button } from '@aaimplatform/ui';
import { createRbacClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/rbac-management';
import { authMiddleware } from '../api/client';
import { formatApiError } from '../lib/api-error';

type Role = components['schemas']['RoleDetail'];
type Permission = components['schemas']['Permission'];

const TIER_LABELS: Record<number, string> = {
  0: 'Tier 0: APEX (Platform-wide)',
  1: 'Tier 1: Core Processor (Multi-tenant)',
  2: 'Tier 2: Tenant (Single FI)',
  3: 'Tier 3: Consumer (Individual)',
  4: 'Tier 4: Delegate (Delegated access)',
};

const CATEGORY_LABELS: Record<string, string> = {
  institutional: 'Institutional Platform',
  consumer: 'Consumer Platform',
  platform: 'Platform Administration',
  aggregate: 'Cross-Platform',
};

export function RoleDetailPage() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const client = createRbacClient();
  // CSRF double-submit (N8) echo + auth-broken bounce on this mutating client.
  client.use(authMiddleware());

  useEffect(() => {
    loadRoleAndPermissions();
  }, [roleId]);

  async function loadRoleAndPermissions() {
    if (!roleId) return;

    try {
      setLoading(true);
      setError(null);

      // Load role with permissions
      const { data: roleData, error: roleError, response: roleResponse } = await client.GET('/api/v1/roles/{roleId}', {
        params: {
          path: { roleId: roleId },
          query: { includePermissions: true, includeHierarchy: true },
        },
      });

      if (roleError) {
        setError(formatApiError(roleError, 'Failed to load role', roleResponse?.status));
        return;
      }

      if (roleData && 'success' in roleData && roleData.success && 'data' in roleData) {
        setRole(roleData.data);
        if (roleData.data.permissions) {
          setSelectedPermissions(new Set(roleData.data.permissions.map((p) => p.id)));
        }
      }

      // Load all available permissions
      const { data: permsData, error: permsError, response: permsResponse } = await client.GET('/api/v1/permissions', {});

      if (permsError) {
        setError(formatApiError(permsError, 'Failed to load permissions', permsResponse?.status));
        return;
      }

      if (permsData && 'success' in permsData && permsData.success && 'data' in permsData) {
        setPermissions(permsData.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }

  async function handleSavePermissions() {
    if (!role) return;

    try {
      setSaving(true);
      setError(null);

      const { data, error: apiError, response } = await client.PUT('/api/v1/roles/{roleId}/permissions', {
        params: { path: { roleId: role.id } },
        body: { permissionIds: Array.from(selectedPermissions) },
      });

      if (apiError) {
        setError(formatApiError(apiError, 'Failed to update permissions', response?.status));
        return;
      }

      if (data && 'success' in data && data.success) {
        // Reload to get updated data
        await loadRoleAndPermissions();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSaving(false);
    }
  }

  function togglePermission(permissionId: string) {
    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    setSelectedPermissions(newSelected);
  }

  function toggleCategory(categoryPerms: Permission[]) {
    const categoryIds = new Set(categoryPerms.map((p) => p.id));
    const allSelected = categoryPerms.every((p) => selectedPermissions.has(p.id));

    const newSelected = new Set(selectedPermissions);
    if (allSelected) {
      // Deselect all
      categoryIds.forEach((id) => newSelected.delete(id));
    } else {
      // Select all
      categoryIds.forEach((id) => newSelected.add(id));
    }
    setSelectedPermissions(newSelected);
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-secondary">Loading role details...</div>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="p-6">
        <Alert type="error" title="Error">
          <div>{error || 'Role not found'}</div>
          <Button variant="ghost" onClick={() => navigate('/roles')} className="mt-4">
            Back to Roles
          </Button>
        </Alert>
      </div>
    );
  }

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    const cat = perm.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  const hasChanges = role.permissions
    ? selectedPermissions.size !== role.permissions.length ||
      !role.permissions.every((p) => selectedPermissions.has(p.id))
    : selectedPermissions.size > 0;

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={() => navigate('/roles')}
        className="flex items-center gap-2 text-sm text-secondary hover:text-primary mb-4 transition-colors"
      >
        <BackIcon size={16} />
        <span>Back to Roles</span>
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-2">
          <div>
            <div className="flex items-center gap-3 text-sm text-secondary">
              <span>{TIER_LABELS[role.tier]}</span>
              {role.providerFamily && (
                <>
                  <span className="text-tertiary">|</span>
                  <span>Provider: {role.providerFamily}</span>
                </>
              )}
              {role.isSystem && (
                <>
                  <span className="text-tertiary">|</span>
                  <span className="text-accent font-medium">System Role</span>
                </>
              )}
            </div>
          </div>
          {!role.isSystem && hasChanges && (
            <Button variant="primary" onClick={handleSavePermissions} disabled={saving}>
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          )}
        </div>
        <p className="text-sm text-secondary">{role.displayName}</p>
      </div>

      {/* Role Information */}
      <Card className="p-6 mb-6">
        <h3 className="text-base font-sans font-semibold text-primary mb-4">Role Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-sans text-tertiary mb-1">Role ID</div>
            <div className="text-sm font-mono text-secondary">{role.id}</div>
          </div>
          <div>
            <div className="text-xs font-sans text-tertiary mb-1">Tier</div>
            <div className="text-sm text-secondary">{TIER_LABELS[role.tier]}</div>
          </div>
          {role.parentRoleId && (
            <div>
              <div className="text-xs font-sans text-tertiary mb-1">Parent Role</div>
              <div className="text-sm text-secondary">{role.parentRoleId}</div>
            </div>
          )}
          {role.childRoles && role.childRoles.length > 0 && (
            <div>
              <div className="text-xs font-sans text-tertiary mb-1">Child Roles</div>
              <div className="text-sm text-secondary">{role.childRoles.length} child role(s)</div>
            </div>
          )}
        </div>
      </Card>

      {/* Permission Matrix */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-sans font-semibold text-primary">
            Permissions ({selectedPermissions.size})
          </h3>
          {role.isSystem && (
            <div className="text-xs text-tertiary bg-surface px-3 py-1 rounded">
              System roles cannot be modified
            </div>
          )}
        </div>

        {Object.entries(permissionsByCategory)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, categoryPerms]) => {
            const allSelected = categoryPerms.every((p) => selectedPermissions.has(p.id));
            const someSelected = categoryPerms.some((p) => selectedPermissions.has(p.id));

            return (
              <div key={category} className="mb-6 last:mb-0">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-sans font-semibold text-secondary">
                    {CATEGORY_LABELS[category] || category.toUpperCase()} ({categoryPerms.length})
                  </h4>
                  {!role.isSystem && (
                    <button
                      onClick={() => toggleCategory(categoryPerms)}
                      className="text-xs text-accent hover:text-accent/80 transition-colors"
                    >
                      {allSelected ? 'Deselect All' : someSelected ? 'Select Remaining' : 'Select All'}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {categoryPerms.map((perm) => (
                    <label
                      key={perm.id}
                      className={`flex items-start gap-2 p-3 rounded border transition-colors ${
                        role.isSystem
                          ? 'bg-surface border-recessed cursor-not-allowed'
                          : 'bg-surface border-recessed hover:border-accent hover:bg-accent/5 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.has(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        disabled={role.isSystem}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-mono text-primary break-all">
                          {perm.permissionString}
                        </div>
                        {perm.description && (
                          <div className="text-xs text-tertiary mt-0.5">{perm.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
      </Card>

      {/* Sticky Save Button for Long Pages */}
      {!role.isSystem && hasChanges && (
        <div className="fixed bottom-6 right-6 z-10">
          <Button variant="primary" onClick={handleSavePermissions} disabled={saving}>
            {saving ? 'Saving...' : `Save ${selectedPermissions.size} Permissions`}
          </Button>
        </div>
      )}
    </div>
  );
}
